import type {
  AgenticApplyStatus,
  AgenticPlanningAttentionCount,
  AgenticPlanningAttentionItem,
  AgenticPlanningAttentionItemType,
  AgenticDraftReviewStatus,
  AgenticPlanningApplyExecution,
  AgenticPlanningProgramSummary,
  AgenticPlanningProgramStatus,
  AgenticPlanningProgramType,
  AgenticPlanningRunComparisonBaseline,
  AgenticPlanningRunComparisonSummary,
  AgenticPlanningDryRunResult,
  AgenticPlanDeliverable,
  AgenticPlanMilestone,
  AgenticPlanProject,
  AgenticPlanTicket,
  AgenticPlanningMode,
  AgenticPlanningRunSpecialistSummary,
  AgenticPlanningSpecialistInfluenceStatus,
  AgenticPlanningSpecialistInvocation,
  AgenticPlanningSpecialistStage,
  AgenticPlanningTrigger,
  AgenticPlanningRunSummary,
  OrganizationAIAgentKickoffSource,
  OrganizationAIAgentOnboardingProfile,
  OrganizationAIAgentRecurringNoteSource,
  OrganizationServiceTabCode,
} from './organizationAIAgentApi';
import type { SkillsTreeNode } from '../../../../features/skills-tree/types';

export const SERVICE_LABELS: Record<OrganizationServiceTabCode, string> = {
  marketing: 'Managed Marketing',
  platform: 'Platform Development',
  assets_design: 'Assets/Design',
};

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const SERVICE_TAB_CODES: Record<OrganizationServiceTabCode, string[]> = {
  marketing: ['marketing', 'svc_marketing'],
  platform: ['platform', 'svc_platform'],
  assets_design: ['assets_design', 'assets/design', 'svc_assets_design', 'design', 'assets'],
};

export type OnboardingSourceReadiness = {
  status: 'strong' | 'partial' | 'thin' | 'missing';
  label: string;
  tone: 'good' | 'warn' | 'danger' | 'neutral';
  summary: string;
  answeredSignals: string[];
  missingSignals: string[];
  noteFieldCount: number;
  profileFieldCount: number;
};

export type PlannerProjectCard = {
  project: AgenticPlanProject;
  milestones: Array<{
    milestone: AgenticPlanMilestone;
    deliverables: AgenticPlanDeliverable[];
    tickets: AgenticPlanTicket[];
  }>;
};

export type PlanningCapabilityState = {
  state:
    | 'ready_to_start'
    | 'latest_plan_available'
    | 'discovery_first'
    | 'insufficient_input_review'
    | 'review_required';
  label: string;
  tone: 'good' | 'warn' | 'danger' | 'neutral' | 'info';
  summary: string;
};

export type PlanningConfidenceState = {
  tone: 'good' | 'warn' | 'danger' | 'neutral' | 'info';
  summary: string;
};

export type PlanningHeaderAction = {
  kind: 'run_onboarding' | 'run_recurring' | 'open_latest_plan';
  label: string;
  scheduleEventIds?: string[];
  runId?: string;
};

export type PlanningHeaderState = {
  state:
    | 'missing_prerequisites'
    | 'first_plan_needed'
    | 'latest_plan_available'
    | 'new_meeting_notes_detected'
    | 'manage_planning';
  title: string;
  description: string;
  contextLine: string;
  primaryAction: PlanningHeaderAction | null;
  newerRecurringNoteCount: number;
};

export type ProgramCardTypeFilter = 'all' | AgenticPlanningProgramType;
export type ProgramCardStatusFilter = 'all' | AgenticPlanningProgramStatus;
export type ProgramCardSortOrder = 'updated' | 'oldest' | 'most_versions' | 'most_attention';

// Legacy initiative-named filter exports remain only for compatibility with older callers.
export type InitiativeCardTypeFilter = ProgramCardTypeFilter;
export type InitiativeCardStatusFilter = ProgramCardStatusFilter;
export type InitiativeCardSortOrder = ProgramCardSortOrder;

export type RunSummaryMetric = {
  label: string;
  value: number;
  tone?: 'good' | 'warn' | 'danger' | 'neutral' | 'info';
};

export type StatusBadge = {
  label: string;
  tone: 'good' | 'warn' | 'danger' | 'neutral' | 'info';
};

export type SummaryApplyReadiness = {
  label: string;
  tone: 'good' | 'warn' | 'danger' | 'neutral' | 'info';
  summary: string;
};

export type ApplyEligibilityState = {
  eligible: boolean;
  label: string;
  tone: 'good' | 'warn' | 'danger' | 'neutral' | 'info';
  summary: string;
  actionLabel?: string | null;
};

export type ComparisonHighlightMetric = {
  label: string;
  left: number;
  right: number;
  delta: number;
  tone: 'good' | 'warn' | 'danger' | 'neutral' | 'info';
};

export type ProgramExecutionSummary = {
  primaryTeamNodeId: string | null;
  primaryTeamLabel: string;
  projectScopeLabel: string;
  projectScopeCount: number;
  collaboratorTeamsLabel: string | null;
  collaboratorRouteLabel: string | null;
  collaboratorCount: number;
};

export type RunVersionInfo = {
  index: number;
  label: string;
  isLatest: boolean;
  isOldest: boolean;
  total: number;
};

export type PlanCardModeFilter = 'all' | 'onboarding' | 'recurring';
export type PlanCardStatusFilter = 'all' | AgenticPlanningRunSummary['status'];
export type PlanCardSortOrder = 'newest' | 'oldest' | 'most_scope' | 'most_attention';

const SUPPORTED_APPLY_ACTIONS = new Set([
  'create_project',
  'update_project',
  'create_milestone',
  'create_ticket',
  'update_ticket',
  'backlog_addition',
  'create_subcategory',
  'create_kbase_process',
]);

function formatTokenLabel(value: string | null | undefined) {
  return asText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function resolveTeamLabelFromId(teamNodeId: string | null | undefined, teamLabelMap: Map<string, string>) {
  const normalized = asText(teamNodeId);
  if (!normalized) return '';
  return teamLabelMap.get(normalized) || formatTokenLabel(normalized);
}

function summarizeLabels(labels: string[]) {
  if (!labels.length) return '';
  if (labels.length === 1) return labels[0];
  return `${labels[0]} + ${labels.length - 1} more`;
}

function uniqueLabels(values: Array<string | null | undefined>) {
  return values
    .map((value) => asText(value))
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index);
}

export function serviceCodeMatchesTab(
  serviceCode: string | null | undefined,
  serviceTab: OrganizationServiceTabCode,
) {
  const normalized = asText(serviceCode).toLowerCase();
  return normalized.length > 0 && SERVICE_TAB_CODES[serviceTab].includes(normalized);
}

export function filterByServiceTab<T>(
  items: T[],
  serviceTab: OrganizationServiceTabCode,
  resolver: (item: T) => string | null | undefined,
) {
  const filtered = items.filter((item) => serviceCodeMatchesTab(resolver(item), serviceTab));
  return filtered.length ? filtered : items;
}

function upsertTeamLabel(labels: Map<string, string>, nodeId: string, nextLabel: string) {
  const normalizedId = asText(nodeId);
  const normalizedLabel = asText(nextLabel);
  if (!normalizedId || !normalizedLabel) return;
  const currentLabel = labels.get(normalizedId);
  if (!currentLabel || currentLabel === normalizedId) {
    labels.set(normalizedId, normalizedLabel);
  }
}

function getBrainNodeLabel(node: SkillsTreeNode) {
  return asText(node.meta?.team) || asText(node.title) || asText(node.meta?.designation) || asText(node.id);
}

export function buildTeamLabelMap(
  result: AgenticPlanningDryRunResult | null,
  brainNodes: SkillsTreeNode[] = [],
) {
  const labels = new Map<string, string>();
  brainNodes.forEach((node) => {
    const nodeId = asText(node?.id);
    if (!nodeId) return;
    upsertTeamLabel(labels, nodeId, getBrainNodeLabel(node));
  });
  if (!result) return labels;
  Object.entries(result.teamNodeLabels ?? {}).forEach(([teamNodeId, label]) => {
    upsertTeamLabel(labels, teamNodeId, label);
  });
  Object.entries(result.runSummary?.teamNodeLabels ?? {}).forEach(([teamNodeId, label]) => {
    upsertTeamLabel(labels, teamNodeId, label);
  });
  const routingNodes = result.contextPack?.routingSignals?.nodes;
  if (Array.isArray(routingNodes)) {
    routingNodes.forEach((node) => {
      const nodeId = asText(node?.nodeId);
      if (!nodeId) return;
      const label =
        asText(node?.teamLabel)
        || asText(node?.title)
        || asText(node?.designationLabel)
        || nodeId;
      upsertTeamLabel(labels, nodeId, label);
    });
  }
  result.teamAssignmentResults?.forEach((assignment) => {
    assignment.sourceSignals.forEach((signal) => {
      upsertTeamLabel(labels, signal.teamNodeId ?? '', signal.designationType ?? '');
    });
  });
  const boundaryMetadata = result.planGraph?.boundaryMetadata;
  boundaryMetadata?.purchasedLanes?.forEach((lane) => {
    upsertTeamLabel(labels, lane.primaryTeamNodeId ?? '', lane.primaryTeamLabel ?? '');
  });
  boundaryMetadata?.executionLanes?.forEach((lane) => {
    upsertTeamLabel(labels, lane.primaryTeamNodeId ?? '', lane.primaryTeamLabel ?? '');
    lane.supportingTeamNodeIds.forEach((teamNodeId, index) => {
      upsertTeamLabel(labels, teamNodeId, lane.supportingTeamLabels?.[index] ?? '');
    });
  });
  return labels;
}

export function formatTeamLabel(teamNodeId: string | null | undefined, teamLabelMap: Map<string, string>) {
  const normalized = asText(teamNodeId);
  if (!normalized) return 'Unresolved';
  return teamLabelMap.get(normalized) || `Unresolved team node (${normalized})`;
}

export function formatTeamList(teamNodeIds: string[], teamLabelMap: Map<string, string>) {
  const labels = teamNodeIds
    .map((teamNodeId) => formatTeamLabel(teamNodeId, teamLabelMap))
    .filter((value, index, all) => Boolean(value) && all.indexOf(value) === index);
  return labels.length ? labels : ['None inferred'];
}

function countProfileFields(profile: OrganizationAIAgentOnboardingProfile | null | undefined) {
  if (!profile) return 0;
  return Object.values(profile).filter((value) => asText(value).length > 0).length;
}

export function getOnboardingSourceReadiness(
  source: OrganizationAIAgentKickoffSource | null,
): OnboardingSourceReadiness {
  if (!source?.note) {
    return {
      status: 'missing',
      label: 'Missing',
      tone: 'neutral',
      summary: 'No persisted onboarding kickoff note with usable discovery content was found yet.',
      answeredSignals: [],
      missingSignals: [
        'Business overview',
        'Client goals',
        'Services in scope',
        'Project framing',
        'Target users / stakeholders',
        'Core workflows',
        'Current state',
        'Constraints / dependencies',
      ],
      noteFieldCount: 0,
      profileFieldCount: 0,
    };
  }

  const profile = source.note.onboardingProfile;
  const signals = [
    ['Business overview', profile?.businessOverview],
    ['Client goals', profile?.clientGoals],
    ['Services in scope', profile?.servicesInScope],
    ['Project framing', profile?.projectOrEngagementType || profile?.immediatePriorities],
    ['Target users / stakeholders', profile?.targetUsersAndRoles || profile?.stakeholders],
    ['Core workflows', profile?.coreWorkflowsJourneys || profile?.firstMilestoneDeliverables],
    ['Current state', profile?.currentState],
    ['Constraints / dependencies', profile?.constraintsDependencies || profile?.clientDependencies],
  ] as const;
  const answeredSignals = signals.filter(([, value]) => asText(value).length > 0).map(([label]) => label);
  const missingSignals = signals.filter(([, value]) => asText(value).length === 0).map(([label]) => label);
  const noteFieldCount = [
    source.note.summary,
    source.note.clientUpdates,
    source.note.decisions,
    source.note.nextSteps,
    source.note.internalContext,
  ].filter((value) => asText(value).length > 0).length;
  const profileFieldCount = countProfileFields(profile);
  const status =
    answeredSignals.length >= 6 ? 'strong' : answeredSignals.length >= 4 ? 'partial' : 'thin';

  return {
    status,
    label: status === 'strong' ? 'Strong' : status === 'partial' ? 'Partial' : 'Thin',
    tone: status === 'strong' ? 'good' : status === 'partial' ? 'warn' : 'danger',
    summary:
      status === 'strong'
        ? 'The latest kickoff note has enough structured discovery context for a confident dry-run plan.'
        : status === 'partial'
          ? 'The latest kickoff note is usable, but the planner may still surface discovery-first behavior or review gates.'
          : 'The latest kickoff note is sparse. Expect discovery-first or insufficient-input behavior.',
    answeredSignals,
    missingSignals,
    noteFieldCount,
    profileFieldCount,
  };
}

export function formatPlanClassificationLabel(value: string | null | undefined) {
  if (value === 'full_build') return 'Full build';
  if (value === 'discovery_first') return 'Discovery first';
  if (value === 'insufficient_input_review') return 'Insufficient input review';
  if (value === 'update_existing_project') return 'Update existing project';
  if (value === 'create_new_project') return 'Create new project';
  if (value === 'backlog_only') return 'Backlog only';
  if (value === 'no_change') return 'No change';
  if (value === 'review_required') return 'Review required';
  return 'Awaiting plan';
}

export function formatProgramTypeLabel(value: AgenticPlanningProgramType | string | null | undefined) {
  if (value === 'plan') return 'Plan';
  if (value === 'product') return 'Product';
  if (value === 'recurring') return 'Plan';
  if (value === 'one_time') return 'Product';
  return 'Program';
}

export function formatProgramStatusLabel(value: AgenticPlanningProgramStatus | string | null | undefined) {
  if (value === 'drafting') return 'Drafting';
  if (value === 'active') return 'Active';
  if (value === 'paused') return 'Paused';
  if (value === 'archived') return 'Archived';
  return 'Unknown';
}

export function toneForProgramStatus(value: AgenticPlanningProgramStatus | string | null | undefined) {
  if (value === 'active') return 'good' as const;
  if (value === 'drafting') return 'warn' as const;
  if (value === 'paused') return 'warn' as const;
  if (value === 'archived') return 'neutral' as const;
  return 'neutral' as const;
}

export function formatPlanClassificationNarrative(value: string | null | undefined) {
  if (value === 'full_build') return 'a full build plan';
  if (value === 'discovery_first') return 'an exploratory follow-up plan';
  if (value === 'insufficient_input_review') return 'an insufficient-input review plan';
  if (value === 'update_existing_project') return 'an update to existing work';
  if (value === 'create_new_project') return 'a net-new project plan';
  if (value === 'backlog_only') return 'a backlog-focused plan';
  if (value === 'no_change') return 'no material work changes';
  if (value === 'review_required') return 'a review-required plan';
  return 'a planner draft';
}

function formatPlanClassificationTitle(value: string | null | undefined) {
  if (value === 'full_build') return 'Initial build plan';
  if (value === 'discovery_first') return 'Discovery-first follow-up';
  if (value === 'insufficient_input_review') return 'Input review draft';
  if (value === 'update_existing_project') return 'Existing work update';
  if (value === 'create_new_project') return 'Net-new work plan';
  if (value === 'backlog_only') return 'Backlog shaping plan';
  if (value === 'no_change') return 'No-change review';
  if (value === 'review_required') return 'Review-required draft';
  return 'Planner draft';
}

function getRunLaneLabels(summary: AgenticPlanningRunSummary) {
  const labels = summary.purchasedLaneSummary
    .map((lane) => (
      asText(lane.serviceTitle)
      || formatTokenLabel(lane.serviceCode)
      || formatTokenLabel(lane.laneKey)
    ))
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index);

  if (labels.length) return labels;

  return [
    summary.mode === 'existing_client_recurring_planning'
      ? 'Recurring planning'
      : 'Onboarding planning',
  ];
}

export function getPlanRunDisplayTitle(summary: AgenticPlanningRunSummary) {
  const programTitle =
    asText(summary.programSummary?.primaryProgramTitle)
    || asText(summary.initiativeSummary?.primaryInitiativeTitle);
  if (programTitle) {
    return `${programTitle} | ${formatPlanClassificationTitle(summary.planClassification)}`;
  }
  const laneLabels = getRunLaneLabels(summary);
  const primaryScope = laneLabels.length > 1
    ? `${laneLabels[0]} + ${laneLabels.length - 1} more`
    : laneLabels[0];
  return `${primaryScope} | ${formatPlanClassificationTitle(summary.planClassification)}`;
}

export function getRunPrimaryProgramId(summary: AgenticPlanningRunSummary | null | undefined) {
  return asText(summary?.programSummary?.primaryProgramId)
    || asText(summary?.initiativeSummary?.primaryInitiativeId)
    || null;
}

export function getRunPrimaryProgramTitle(summary: AgenticPlanningRunSummary | null | undefined) {
  return asText(summary?.programSummary?.primaryProgramTitle)
    || asText(summary?.initiativeSummary?.primaryInitiativeTitle)
    || null;
}

export function getProgramScopeLabel(program: AgenticPlanningProgramSummary | null | undefined) {
  return asText(program?.primaryScopeLabel)
    || asText(program?.primaryServiceTitle)
    || formatTokenLabel(program?.primaryScopeCode)
    || formatTokenLabel(program?.primaryServiceCode)
    || formatTokenLabel(program?.primaryLaneKey)
    || 'Offering scope pending';
}

function findProgramPurchasedLane(
  program: AgenticPlanningProgramSummary | null | undefined,
  summary: AgenticPlanningRunSummary | null | undefined,
  result: AgenticPlanningDryRunResult | null | undefined,
) {
  const scopeCode = asText(program?.primaryScopeCode) || asText(summary?.programSummary?.primaryScopeCode);
  const subscriptionId = asText(program?.primarySubscriptionId) || asText(summary?.programSummary?.primarySubscriptionId);
  const serviceCode = asText(program?.primaryServiceCode);
  const candidateLanes = [
    ...(result?.planGraph.boundaryMetadata?.purchasedLanes ?? []),
    ...(summary?.purchasedLaneSummary ?? []),
  ];
  return candidateLanes.find((lane) => (
    (scopeCode && lane.selectedOfferingCodes.includes(scopeCode))
    || (subscriptionId && asText(lane.subscriptionId) === subscriptionId)
    || (serviceCode && asText(lane.serviceCode) === serviceCode)
  )) ?? null;
}

function getProjectScopeLabels(result: AgenticPlanningDryRunResult | null | undefined) {
  const projectSubcategories = uniqueLabels(
    (result?.planGraph.projects ?? []).map((project) => formatTokenLabel(project.offeringSubcategoryId)),
  );
  if (projectSubcategories.length) return projectSubcategories;
  const ticketSubcategories = uniqueLabels(
    (result?.planGraph.tickets ?? []).map((ticket) => formatTokenLabel(ticket.offeringSubcategoryId)),
  );
  if (ticketSubcategories.length) return ticketSubcategories;
  return uniqueLabels((result?.planGraph.projects ?? []).map((project) => project.name));
}

function collectCollaboratorRoutes(
  result: AgenticPlanningDryRunResult | null | undefined,
  primaryTeamNodeId: string | null,
  teamLabelMap: Map<string, string>,
) {
  const teamLabels: string[] = [];
  const routeLabels: string[] = [];

  const pushRoute = (
    teamNodeId: string | null | undefined,
    executionServiceCode: string | null | undefined,
    executionOfferingCode: string | null | undefined,
    executionOfferingSubcategoryId: string | null | undefined,
  ) => {
    const normalizedTeamNodeId = asText(teamNodeId);
    const executionRouteParts = uniqueLabels([
      formatTokenLabel(executionOfferingCode),
      formatTokenLabel(executionOfferingSubcategoryId),
      formatTokenLabel(executionServiceCode),
    ]);
    const hasExecutionRoute = executionRouteParts.length > 0;
    const isCollaboratorTeam =
      normalizedTeamNodeId.length > 0
      && (!primaryTeamNodeId || normalizedTeamNodeId !== primaryTeamNodeId);
    if (!hasExecutionRoute && !isCollaboratorTeam) return;
    const teamLabel = resolveTeamLabelFromId(normalizedTeamNodeId, teamLabelMap);
    if (teamLabel) {
      teamLabels.push(teamLabel);
      routeLabels.push(
        executionRouteParts.length
          ? `${teamLabel} via ${executionRouteParts.join(' / ')}`
          : teamLabel,
      );
      return;
    }
    if (executionRouteParts.length) {
      routeLabels.push(executionRouteParts.join(' / '));
    }
  };

  (result?.planGraph.projects ?? []).forEach((project) => {
    pushRoute(
      project.teamNodeId,
      project.executionServiceCode,
      project.executionOfferingCode,
      project.executionOfferingSubcategoryId,
    );
  });

  if (!routeLabels.length) {
    (result?.planGraph.tickets ?? []).forEach((ticket) => {
      pushRoute(
        ticket.proposedExecutionTeamNodeId,
        ticket.executionServiceCode,
        ticket.executionOfferingCode,
        ticket.executionOfferingSubcategoryId,
      );
    });
  }

  return {
    teamLabels: uniqueLabels(teamLabels),
    routeLabels: uniqueLabels(routeLabels),
  };
}

export function getProgramExecutionSummary({
  program,
  summary,
  result,
  teamLabelMap = new Map<string, string>(),
}: {
  program: AgenticPlanningProgramSummary | null | undefined;
  summary?: AgenticPlanningRunSummary | null | undefined;
  result?: AgenticPlanningDryRunResult | null | undefined;
  teamLabelMap?: Map<string, string>;
}): ProgramExecutionSummary {
  const purchasedLane = findProgramPurchasedLane(program, summary, result);
  const primaryTeamNodeId =
    asText(program?.primaryTeamNodeId)
    || asText(summary?.programSummary?.primaryTeamNodeId)
    || asText(purchasedLane?.primaryTeamNodeId)
    || asText(result?.runSummary.primaryTeamNodeIds?.[0])
    || asText(summary?.primaryTeamNodeIds?.[0])
    || null;
  const primaryTeamLabel =
    asText(program?.primaryTeamLabel)
    || asText(summary?.programSummary?.primaryTeamLabel)
    || asText(purchasedLane?.primaryTeamLabel)
    || resolveTeamLabelFromId(primaryTeamNodeId, teamLabelMap)
    || 'Team pending';
  const projectScopeLabels = getProjectScopeLabels(result);
  const collaboratorRoutes = collectCollaboratorRoutes(result, primaryTeamNodeId, teamLabelMap);

  return {
    primaryTeamNodeId,
    primaryTeamLabel,
    projectScopeLabel: summarizeLabels(projectScopeLabels) || 'Project scope pending',
    projectScopeCount: projectScopeLabels.length,
    collaboratorTeamsLabel: summarizeLabels(collaboratorRoutes.teamLabels) || null,
    collaboratorRouteLabel: summarizeLabels(collaboratorRoutes.routeLabels) || null,
    collaboratorCount: collaboratorRoutes.routeLabels.length,
  };
}

// Legacy initiative-named helpers remain exported only for compatibility with older callers.
export const formatInitiativeTypeLabel = formatProgramTypeLabel;
export const formatInitiativeStatusLabel = formatProgramStatusLabel;
export const toneForInitiativeStatus = toneForProgramStatus;
export const getRunPrimaryInitiativeId = getRunPrimaryProgramId;
export const getRunPrimaryInitiativeTitle = getRunPrimaryProgramTitle;
export const getInitiativeScopeLabel = getProgramScopeLabel;

export function formatPlanningModeLabel(value: AgenticPlanningMode | string | null | undefined) {
  if (value === 'onboarding_initial_build') return 'Onboarding planning';
  if (value === 'existing_client_recurring_planning') return 'Recurring-note planning';
  return 'Planning';
}

export function formatDraftReviewStatusLabel(value: AgenticDraftReviewStatus | string | null | undefined) {
  if (value === 'approved_draft') return 'Approved draft';
  if (value === 'rejected_draft') return 'Rejected draft';
  if (value === 'needs_revision') return 'Needs revision';
  if (value === 'superseded') return 'Superseded';
  if (value === 'review_required') return 'Review required';
  return 'Review pending';
}

export function toneForDraftReviewStatus(value: AgenticDraftReviewStatus | string | null | undefined) {
  if (value === 'approved_draft') return 'good' as const;
  if (value === 'rejected_draft') return 'danger' as const;
  if (value === 'needs_revision') return 'warn' as const;
  if (value === 'superseded') return 'warn' as const;
  if (value === 'review_required') return 'warn' as const;
  return 'neutral' as const;
}

export function formatApplyStatusLabel(value: AgenticApplyStatus | string | null | undefined) {
  if (value === 'partially_failed') return 'Partially failed';
  if (value === 'pending') return 'Pending';
  if (value === 'running') return 'Running';
  if (value === 'succeeded') return 'Succeeded';
  if (value === 'failed') return 'Failed';
  return 'Not started';
}

export function toneForApplyStatus(value: AgenticApplyStatus | string | null | undefined) {
  if (value === 'succeeded') return 'good' as const;
  if (value === 'running') return 'info' as const;
  if (value === 'pending') return 'info' as const;
  if (value === 'partially_failed') return 'warn' as const;
  if (value === 'failed') return 'danger' as const;
  return 'neutral' as const;
}

export function formatAttentionItemTypeLabel(
  value: AgenticPlanningAttentionItemType | string | null | undefined,
) {
  if (value === 'draft_needs_review') return 'Draft needs review';
  if (value === 'approved_ready_to_apply') return 'Approved and ready to apply';
  if (value === 'approved_apply_blocked') return 'Approved but blocked from apply';
  if (value === 'apply_failed') return 'Apply failed';
  if (value === 'apply_partially_failed') return 'Apply partially failed';
  if (value === 'new_note_after_latest_plan') return 'New note after latest plan';
  if (value === 'plan_needs_revision') return 'Plan needs revision';
  return 'Attention item';
}

export function formatAttentionStatusLabel(value: string | null | undefined) {
  if (value === 'open') return 'Open';
  if (value === 'resolved') return 'Resolved';
  if (value === 'dismissed') return 'Dismissed';
  if (value === 'obsolete') return 'Obsolete';
  return 'Unknown';
}

export function getAttentionItemTone(item: AgenticPlanningAttentionItem) {
  if (item.status !== 'open') return 'neutral' as const;
  if (item.itemType === 'approved_ready_to_apply') return 'good' as const;
  if (item.itemType === 'apply_failed' || item.itemType === 'approved_apply_blocked') {
    return 'danger' as const;
  }
  if (item.itemType === 'apply_partially_failed' || item.itemType === 'plan_needs_revision') {
    return 'warn' as const;
  }
  if (item.itemType === 'new_note_after_latest_plan') return 'info' as const;
  if (item.priority === 'high') return 'warn' as const;
  return 'warn' as const;
}

export function summarizeAttentionCounts(counts: AgenticPlanningAttentionCount[]) {
  const openCounts = counts.filter((entry) => entry.status === 'open');
  const totalOpen = openCounts.reduce((total, entry) => total + entry.count, 0);
  const highPriorityOpen = openCounts
    .filter((entry) => entry.itemType === 'apply_failed' || entry.itemType === 'approved_apply_blocked')
    .reduce((total, entry) => total + entry.count, 0);
  return {
    totalOpen,
    highPriorityOpen,
    labels: openCounts.map((entry) => `${formatAttentionItemTypeLabel(entry.itemType)} (${entry.count})`),
  };
}

export function formatComparisonBaselineLabel(
  value: AgenticPlanningRunComparisonBaseline | string | null | undefined,
) {
  if (value === 'previous') return 'Previous run';
  if (value === 'latest_approved') return 'Latest approved';
  if (value === 'nearest_meaningful') return 'Nearest meaningful baseline';
  return 'Baseline';
}

export function formatComparisonTypeLabel(value: string | null | undefined) {
  if (value === 'latest_vs_previous') return 'Latest vs previous';
  if (value === 'latest_vs_latest_approved') return 'Latest vs latest approved';
  if (value === 'nearest_meaningful_baseline') return 'Nearest meaningful baseline';
  if (value === 'arbitrary_pair') return 'Explicit run pair';
  return 'Run comparison';
}

export function formatComparabilityLevelLabel(value: string | null | undefined) {
  if (value === 'high') return 'High confidence';
  if (value === 'medium') return 'Medium confidence';
  if (value === 'low') return 'Low confidence';
  return 'Unknown confidence';
}

export function getComparisonHighlightMetrics(
  summary: AgenticPlanningRunComparisonSummary | null | undefined,
): ComparisonHighlightMetric[] {
  const comparison = summary?.comparison;
  if (!summary?.baselineAvailable || !comparison) return [];
  const metrics: ComparisonHighlightMetric[] = [
    {
      label: 'Projects',
      left: comparison.summaryDelta.counts.projects.left,
      right: comparison.summaryDelta.counts.projects.right,
      delta: comparison.summaryDelta.counts.projects.delta,
      tone: comparison.summaryDelta.counts.projects.delta === 0 ? 'neutral' : comparison.summaryDelta.counts.projects.delta > 0 ? 'good' : 'warn',
    },
    {
      label: 'Tickets',
      left: comparison.summaryDelta.counts.tickets.left,
      right: comparison.summaryDelta.counts.tickets.right,
      delta: comparison.summaryDelta.counts.tickets.delta,
      tone: comparison.summaryDelta.counts.tickets.delta === 0 ? 'neutral' : comparison.summaryDelta.counts.tickets.delta > 0 ? 'good' : 'warn',
    },
    {
      label: 'Backlog',
      left: comparison.summaryDelta.counts.backlogItems.left,
      right: comparison.summaryDelta.counts.backlogItems.right,
      delta: comparison.summaryDelta.counts.backlogItems.delta,
      tone: comparison.summaryDelta.counts.backlogItems.delta === 0 ? 'neutral' : 'warn',
    },
    {
      label: 'Warnings',
      left: comparison.summaryDelta.counts.warnings.left,
      right: comparison.summaryDelta.counts.warnings.right,
      delta: comparison.summaryDelta.counts.warnings.delta,
      tone: comparison.summaryDelta.counts.warnings.delta === 0 ? 'neutral' : comparison.summaryDelta.counts.warnings.delta > 0 ? 'warn' : 'good',
    },
  ];
  return metrics;
}

export function getPlanOutcomeSummary(
  summary: Pick<AgenticPlanningRunSummary, 'mode' | 'planClassification'> | null | undefined,
) {
  if (!summary) {
    return 'Planner outcome becomes clearer once a draft is selected.';
  }
  if (summary.planClassification === 'update_existing_project') {
    return 'This run updates existing work instead of creating parallel delivery scope.';
  }
  if (summary.planClassification === 'create_new_project') {
    return 'This run proposes net-new scoped work from the latest client context.';
  }
  if (summary.planClassification === 'backlog_only') {
    return 'This run captures follow-on work as backlog instead of immediate delivery.';
  }
  if (summary.planClassification === 'no_change') {
    return 'This run found no meaningful planning change from the latest source context.';
  }
  if (summary.planClassification === 'discovery_first') {
    return 'This run stays exploratory and intentionally avoids immediate executable scope.';
  }
  if (summary.planClassification === 'insufficient_input_review') {
    return 'The planner withheld a more concrete draft because the available inputs are still too thin.';
  }
  if (summary.planClassification === 'review_required') {
    return 'The planner found meaningful scope but withheld a cleaner path until a reviewer resolves ambiguity.';
  }
  if (summary.mode === 'existing_client_recurring_planning') {
    return 'This recurring run reconciles new note context against existing work, backlog, and current ownership.';
  }
  return 'This onboarding run is shaping the first draft plan from kickoff discovery context.';
}

export function formatSpecialistStageLabel(value: AgenticPlanningSpecialistStage | string | null | undefined) {
  if (value === 'discovery') return 'Discovery';
  if (value === 'planning') return 'Planning';
  if (value === 'validation') return 'Validation';
  return 'Specialist stage';
}

export function formatSpecialistInfluenceStatusLabel(
  value: AgenticPlanningSpecialistInfluenceStatus | string | null | undefined,
) {
  if (value === 'applied') return 'Applied';
  if (value === 'partially_applied') return 'Partially applied';
  if (value === 'ignored') return 'Ignored';
  return 'Unknown';
}

export function getSpecialistInfluenceSummary(params: {
  summary?: AgenticPlanningRunSpecialistSummary | null;
  invocations?: AgenticPlanningSpecialistInvocation[] | null;
}) {
  const appliedCount = params.summary?.appliedCount ?? 0;
  const partialCount = params.summary?.partiallyAppliedCount ?? 0;
  const applicableCount = params.summary?.applicableCount ?? 0;
  if (!applicableCount && !(params.invocations?.length ?? 0)) {
    return {
      label: 'No specialist influence',
      tone: 'neutral' as const,
      summary: 'This run stayed on the core planner path without service-specific specialist adjustments.',
    };
  }
  if (appliedCount > 0 || partialCount > 0) {
    return {
      label: 'Specialist influence recorded',
      tone: 'info' as const,
      summary:
        params.summary?.influenceSummaries[0]
        || 'One or more service specialists enriched discovery, planning, or validation on this run.',
    };
  }
  return {
    label: 'Specialist considered',
    tone: 'warn' as const,
    summary: 'A specialist matched this run, but its suggestions were ignored or not needed in the final result.',
  };
}

export function getNewerRecurringNoteCountForRun(
  summary: AgenticPlanningRunSummary | null | undefined,
  recurringNoteSources: OrganizationAIAgentRecurringNoteSource[],
) {
  if (!summary) return 0;
  const runTimestamp = toMillis(summary.updatedAt);
  if (!runTimestamp) return 0;
  return recurringNoteSources.filter((source) => toMillis(source.note.updatedAt) > runTimestamp).length;
}

export function getRunTriggerSummary(
  summary: AgenticPlanningRunSummary,
  trigger: AgenticPlanningTrigger | null | undefined,
) {
  if (!trigger) {
    return summary.mode === 'existing_client_recurring_planning'
      ? 'Started manually from recurring notes'
      : 'Started manually from kickoff notes';
  }
  const sourceLabel =
    trigger.sourceKind === 'kickoff'
      ? 'Started from kickoff note'
      : trigger.sourceKind === 'recurring'
        ? 'Started from recurring note'
        : 'Started from planner trigger';
  const meetingType = asText(trigger.sourceMeetingType);
  return meetingType ? `${sourceLabel} | ${formatTokenLabel(meetingType)}` : sourceLabel;
}

export function getRunSummaryApplyReadiness(summary: AgenticPlanningRunSummary): SummaryApplyReadiness {
  const reviewStatus = summary.draftReview?.reviewStatus;
  if (reviewStatus === 'approved_draft') {
    return {
      label: 'Apply eligible',
      tone: 'good',
      summary: 'This draft is approved and can move to explicit apply execution.',
    };
  }
  if (reviewStatus === 'superseded') {
    return {
      label: 'Not current',
      tone: 'warn',
      summary: 'A newer approved or current draft replaced this run.',
    };
  }
  if (reviewStatus === 'rejected_draft') {
    return {
      label: 'Not applyable',
      tone: 'danger',
      summary: 'Rejected drafts cannot be applied.',
    };
  }
  if (reviewStatus === 'needs_revision') {
    return {
      label: 'Revise before apply',
      tone: 'warn',
      summary: 'This draft needs revision before it can be approved and applied.',
    };
  }
  return {
    label: 'Review first',
    tone: 'neutral',
    summary: 'Approval is still required before any explicit apply action is available.',
  };
}

export function getApplyEligibilityState(params: {
  result: AgenticPlanningDryRunResult | null;
  latestApply: AgenticPlanningApplyExecution | null;
}) : ApplyEligibilityState {
  const { result, latestApply } = params;
  if (!result) {
    return {
      eligible: false,
      label: 'Load a plan',
      tone: 'neutral',
      summary: 'Apply eligibility becomes available after the selected draft is loaded.',
      actionLabel: null,
    };
  }

  if (latestApply?.status === 'succeeded') {
    return {
      eligible: false,
      label: 'Already applied',
      tone: 'good',
      summary: 'This draft already has a successful apply attempt recorded.',
      actionLabel: null,
    };
  }

  if (latestApply?.status === 'running' || latestApply?.status === 'pending') {
    return {
      eligible: false,
      label: 'Apply in progress',
      tone: 'info',
      summary: 'An apply attempt is already pending or running for this draft.',
      actionLabel: null,
    };
  }

  const reviewStatus = result.runSummary.draftReview?.reviewStatus;
  if (reviewStatus === 'superseded') {
    return {
      eligible: false,
      label: 'Superseded',
      tone: 'warn',
      summary: 'Superseded drafts are not eligible for apply.',
      actionLabel: null,
    };
  }
  if (reviewStatus !== 'approved_draft') {
    return {
      eligible: false,
      label: 'Approval required',
      tone: toneForDraftReviewStatus(reviewStatus),
      summary: `This draft is currently ${formatDraftReviewStatusLabel(reviewStatus).toLowerCase()} and cannot be applied yet.`,
      actionLabel: null,
    };
  }

  if (!result.writePlan.operations.length) {
    return {
      eligible: false,
      label: 'Nothing to apply',
      tone: 'neutral',
      summary: 'The approved draft does not contain any executable write operations.',
      actionLabel: null,
    };
  }

  const unsupportedActions = result.writePlan.operations
    .map((operation) => operation.action)
    .filter((action) => !SUPPORTED_APPLY_ACTIONS.has(action));

  if (unsupportedActions.length) {
    return {
      eligible: false,
      label: 'Deferred operations present',
      tone: 'warn',
      summary: `This draft includes deferred write actions: ${unsupportedActions.join(', ')}.`,
      actionLabel: null,
    };
  }

  if (latestApply?.status === 'failed' || latestApply?.status === 'partially_failed') {
    return {
      eligible: true,
      label: 'Retry apply',
      tone: latestApply.status === 'failed' ? 'danger' : 'warn',
      summary: 'A prior apply attempt did not finish cleanly. You can retry the approved draft explicitly.',
      actionLabel: 'Retry apply',
    };
  }

  return {
    eligible: true,
    label: 'Apply ready',
    tone: 'good',
    summary: 'This approved draft is eligible for explicit apply execution.',
    actionLabel: 'Apply approved draft',
  };
}

export function getPlanCardBadges(params: {
  summary: AgenticPlanningRunSummary;
  latestRunId?: string | null;
  currentRunId?: string | null;
  latestApprovedRunId?: string | null;
  newerRecurringNoteCount?: number;
  attentionCount?: number;
  specialistAppliedCount?: number;
}) {
  const badges: StatusBadge[] = [];
  if (params.summary.runId === params.latestRunId) {
    badges.push({ label: 'Latest', tone: 'good' });
  }
  if (params.summary.runId === params.currentRunId) {
    badges.push({ label: 'Current draft', tone: 'info' });
  }
  if (params.summary.runId === params.latestApprovedRunId) {
    badges.push({ label: 'Latest approved', tone: 'good' });
  }
  if (params.summary.draftReview?.reviewStatus === 'superseded') {
    badges.push({ label: 'Superseded', tone: 'warn' });
  }
  if ((params.newerRecurringNoteCount ?? 0) > 0) {
    badges.push({
      label: `${params.newerRecurringNoteCount} newer note${params.newerRecurringNoteCount === 1 ? '' : 's'}`,
      tone: 'warn',
    });
  }
  if ((params.attentionCount ?? 0) > 0) {
    badges.push({
      label: `${params.attentionCount} action item${params.attentionCount === 1 ? '' : 's'}`,
      tone: 'warn',
    });
  }
  if ((params.specialistAppliedCount ?? 0) > 0) {
    badges.push({
      label: 'Specialist',
      tone: 'info',
    });
  }
  return badges;
}

export function getPlanningCapabilityState(
  result: AgenticPlanningDryRunResult | null,
  sourceReadiness: OnboardingSourceReadiness,
): PlanningCapabilityState {
  if (!result) {
    return {
      state: 'ready_to_start',
      label: sourceReadiness.status === 'missing' ? 'Kickoff source needed' : 'Ready to start planning',
      tone: sourceReadiness.status === 'missing' ? 'neutral' : sourceReadiness.tone,
      summary:
        sourceReadiness.status === 'missing'
          ? 'Onboarding planning becomes available after a usable intro-call note is persisted.'
          : 'Onboarding planning is available now. Starting a plan creates a draft preview only and does not write projects or tickets.',
    };
  }
  if (result.approvalGate.status === 'review_required') {
    return {
      state: 'review_required',
      label: 'Review required',
      tone: 'warn',
      summary: 'A draft plan is available, but review is still required before any later execution mode could exist.',
    };
  }
  const planMode = result.planGraph.modeMetadata.inputSufficiency?.planMode;
  if (planMode === 'discovery_first') {
    return {
      state: 'discovery_first',
      label: 'Discovery-first plan available',
      tone: 'warn',
      summary: 'The planner found enough signal to outline the work, but it is intentionally keeping discovery in front of build execution.',
    };
  }
  if (planMode === 'insufficient_input_review') {
    return {
      state: 'insufficient_input_review',
      label: 'Insufficient-input result',
      tone: 'danger',
      summary: 'The planner withheld a fuller build draft because the kickoff inputs are still too thin.',
    };
  }
  return {
    state: 'latest_plan_available',
    label: 'Latest plan available',
    tone: 'good',
    summary: 'A current onboarding plan draft is available for operator review.',
  };
}

export function getLatestHistorySummaryLabel(summary: AgenticPlanningRunSummary) {
  return summary.summaryText
    || (
      summary.mode === 'existing_client_recurring_planning'
        ? `${formatPlanClassificationLabel(summary.planClassification)} | ${summary.counts.projectUpdates ?? 0} updates | ${summary.counts.projects} proposed projects | ${summary.counts.backlogItems ?? 0} backlog`
        : `${formatPlanClassificationLabel(summary.planClassification)} | ${summary.counts.projects} projects | ${summary.counts.tickets} tickets`
    );
}

export function buildRunVersionMap(history: AgenticPlanningRunSummary[]) {
  const sorted = [...history]
    .sort((left, right) => {
      const createdDelta = new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
      if (createdDelta !== 0) return createdDelta;
      const updatedDelta = new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime();
      if (updatedDelta !== 0) return updatedDelta;
      return left.runId.localeCompare(right.runId);
    });
  const total = sorted.length;
  return new Map<string, RunVersionInfo>(
    sorted.map((entry, index) => [
      entry.runId,
      {
        index,
        label: `v${index}`,
        isLatest: index === total - 1,
        isOldest: index === 0,
        total,
      },
    ]),
  );
}

export function humanizeComparisonCopy(value: string | null | undefined) {
  const normalized = asText(value);
  if (!normalized) return '';
  const shiftedMatch = normalized.match(/Target run shifted from ([a-z_]+) to ([a-z_]+)\.?/i);
  if (shiftedMatch) {
    return `Planner recommendation changed from ${formatPlanClassificationNarrative(shiftedMatch[1])} to ${formatPlanClassificationNarrative(shiftedMatch[2])}.`;
  }
  const classificationMatch = normalized.match(/Classification changed from ([a-z_]+) to ([a-z_]+)\.?/i);
  if (classificationMatch) {
    return `Planner recommendation changed from ${formatPlanClassificationNarrative(classificationMatch[1])} to ${formatPlanClassificationNarrative(classificationMatch[2])}.`;
  }
  return normalized.replace(/\b(full_build|discovery_first|insufficient_input_review|update_existing_project|create_new_project|backlog_only|no_change|review_required)\b/g, (token) => formatPlanClassificationLabel(token).toLowerCase());
}

export function getPlanningConfidenceState(
  result: AgenticPlanningDryRunResult | null,
): PlanningConfidenceState {
  if (!result) {
    return {
      tone: 'neutral',
      summary: 'Planning confidence becomes available after a draft run is loaded.',
    };
  }
  const validationResults = result.validationResults ?? [];
  const factConfidenceSummary = result.factSet?.confidenceSummary;
  const planConfidenceWarn = validationResults.some(
    (entry) => entry.ruleName === 'plan_confidence_gate' && entry.status === 'warn',
  );
  const factConfidenceWarn = validationResults.some(
    (entry) => entry.ruleName === 'fact_confidence_gate' && entry.status === 'warn',
  ) || factConfidenceSummary?.needsHumanReview;

  if (planConfidenceWarn) {
    return {
      tone: 'warn',
      summary: 'Planning confidence is still below the current slice-0 review threshold.',
    };
  }
  if (factConfidenceWarn) {
    return {
      tone: 'warn',
      summary: 'Planning confidence is strong, but fact review remains open for this run.',
    };
  }
  return {
    tone: 'good',
    summary: 'Planning confidence and fact review both cleared the current slice-0 review gates.',
  };
}

export function groupProjectsForDisplay(
  result: AgenticPlanningDryRunResult,
  serviceTab?: OrganizationServiceTabCode | null,
): PlannerProjectCard[] {
  const visibleProjects = serviceTab
    ? filterByServiceTab(
        result.planGraph.projects,
        serviceTab,
        (project) => project.serviceCode,
      )
    : result.planGraph.projects;
  return visibleProjects.map((project) => ({
    project,
    milestones: result.planGraph.milestones
      .filter((milestone) => milestone.projectKey === project.key)
      .map((milestone) => ({
        milestone,
        deliverables: (result.planGraph.deliverables ?? []).filter((deliverable) => deliverable.milestoneKey === milestone.key),
        tickets: (result.planGraph.tickets ?? []).filter((ticket) => ticket.milestoneKey === milestone.key),
      })),
  }));
}

export function getImmediateScopeCounts(result: AgenticPlanningDryRunResult) {
  const immediateKeys = new Set(result.planGraph.boundaryMetadata?.immediateScopeKeys ?? []);
  const backlogKeys = new Set(result.planGraph.boundaryMetadata?.backlogScopeKeys ?? []);
  return {
    immediateProjects: result.planGraph.projects.filter((project) => immediateKeys.has(project.key)).length,
    immediateTickets: result.planGraph.tickets.filter((ticket) => immediateKeys.has(ticket.key)).length,
    backlogProjects: result.planGraph.projects.filter((project) => backlogKeys.has(project.key)).length,
    backlogTickets: result.planGraph.tickets.filter((ticket) => backlogKeys.has(ticket.key)).length,
  };
}

function formatCompactDate(value: string | null | undefined) {
  const normalized = asText(value);
  if (!normalized) return 'not found';
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function toMillis(value: string | null | undefined) {
  const normalized = asText(value);
  if (!normalized) return 0;
  const timestamp = new Date(normalized).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function uniqueEventIds(sources: OrganizationAIAgentRecurringNoteSource[], limit = 3) {
  return sources
    .map((source) => asText(source.event.id))
    .filter((value, index, all) => Boolean(value) && all.indexOf(value) === index)
    .slice(0, limit);
}

export function getRunSummaryMetrics(summary: AgenticPlanningRunSummary | null): RunSummaryMetric[] {
  if (!summary) return [];
  if (summary.mode === 'existing_client_recurring_planning') {
    return [
      {
        label: 'Proposed',
        value: summary.counts.projects,
      },
      {
        label: 'Updates',
        value: (summary.counts.projectUpdates ?? 0) + (summary.counts.ticketUpdates ?? 0),
      },
      {
        label: 'Backlog',
        value: summary.counts.backlogItems ?? 0,
        tone: (summary.counts.backlogItems ?? 0) > 0 ? 'warn' : 'neutral',
      },
    ];
  }
  return [
    {
      label: 'Projects',
      value: summary.counts.projects,
    },
    {
      label: 'Tickets',
      value: summary.counts.tickets,
    },
    {
      label: 'Review',
      value: summary.counts.reviewRequiredItems,
      tone: summary.counts.reviewRequiredItems ? 'warn' : 'good',
    },
  ];
}

function getRunSummaryScopeScore(summary: AgenticPlanningRunSummary) {
  return (
    (summary.counts.projects ?? 0) * 100
    + (summary.counts.projectUpdates ?? 0) * 90
    + (summary.counts.milestones ?? 0) * 20
    + (summary.counts.tickets ?? 0) * 10
    + (summary.counts.ticketUpdates ?? 0) * 9
    + (summary.counts.backlogItems ?? 0) * 4
  );
}

function getRunSummaryAttentionScore(summary: AgenticPlanningRunSummary) {
  const statusWeight =
    summary.status === 'blocked'
      ? 10000
      : summary.status === 'review_required'
        ? 5000
        : 0;
  const reviewWeight =
    summary.draftReview?.reviewStatus === 'rejected_draft'
      ? 2200
      : summary.draftReview?.reviewStatus === 'needs_revision'
        ? 1800
        : summary.draftReview?.reviewStatus === 'review_required'
          ? 1400
          : summary.draftReview?.reviewStatus === 'superseded'
            ? 600
            : 0;
  return (
    statusWeight
    + reviewWeight
    + (summary.counts.failures ?? 0) * 400
    + (summary.counts.reviewRequiredItems ?? 0) * 120
    + (summary.counts.warnings ?? 0) * 25
    + (summary.counts.fallbackLanes ?? 0) * 10
  );
}

function getRunSummarySearchText(summary: AgenticPlanningRunSummary) {
  return [
    summary.summaryText,
    getLatestHistorySummaryLabel(summary),
    formatPlanningModeLabel(summary.mode),
    formatPlanClassificationLabel(summary.planClassification),
    summary.status.replace(/_/g, ' '),
    formatDraftReviewStatusLabel(summary.draftReview?.reviewStatus),
    ...summary.sourceRefs,
    ...summary.sourceEventIds,
    ...summary.purchasedLaneSummary.flatMap((lane) => [lane.serviceTitle, lane.serviceCode, lane.laneKey]),
  ]
    .map((value) => asText(value).toLowerCase())
    .filter(Boolean)
    .join(' ');
}

export function filterAndSortPlanCards(
  summaries: AgenticPlanningRunSummary[],
  options: {
    query?: string;
    modeFilter?: PlanCardModeFilter;
    statusFilter?: PlanCardStatusFilter;
    sortOrder?: PlanCardSortOrder;
  },
) {
  const normalizedQuery = asText(options.query).toLowerCase();
  const modeFilter = options.modeFilter ?? 'all';
  const statusFilter = options.statusFilter ?? 'all';
  const sortOrder = options.sortOrder ?? 'newest';

  const filtered = summaries.filter((summary) => {
    if (modeFilter === 'onboarding' && summary.mode !== 'onboarding_initial_build') return false;
    if (modeFilter === 'recurring' && summary.mode !== 'existing_client_recurring_planning') return false;
    if (statusFilter !== 'all' && summary.status !== statusFilter) return false;
    if (normalizedQuery && !getRunSummarySearchText(summary).includes(normalizedQuery)) return false;
    return true;
  });

  return [...filtered].sort((left, right) => {
    if (sortOrder === 'oldest') {
      return toMillis(left.updatedAt) - toMillis(right.updatedAt);
    }
    if (sortOrder === 'most_scope') {
      return getRunSummaryScopeScore(right) - getRunSummaryScopeScore(left)
        || toMillis(right.updatedAt) - toMillis(left.updatedAt);
    }
    if (sortOrder === 'most_attention') {
      return getRunSummaryAttentionScore(right) - getRunSummaryAttentionScore(left)
        || toMillis(right.updatedAt) - toMillis(left.updatedAt);
    }
    return toMillis(right.updatedAt) - toMillis(left.updatedAt);
  });
}

export function getPlanningHeaderState(params: {
  orgName: string;
  ownerEmail: string | null;
  serviceTab: OrganizationServiceTabCode;
  hasActiveServiceContract: boolean;
  projectCount: number;
  programCount: number;
  kickoffSource: OrganizationAIAgentKickoffSource | null;
  recurringNoteSources: OrganizationAIAgentRecurringNoteSource[];
  latestRunSummary: AgenticPlanningRunSummary | null;
  historyCount: number;
}): PlanningHeaderState {
  const hasKickoffSource = Boolean(params.kickoffSource?.event?.id && params.kickoffSource?.note);
  const latestRecurringSource = params.recurringNoteSources[0] ?? null;
  const latestPlanTimestamp = toMillis(params.latestRunSummary?.updatedAt);
  const newerRecurringSources = latestPlanTimestamp > 0
    ? params.recurringNoteSources.filter((source) => toMillis(source.note.updatedAt) > latestPlanTimestamp)
    : [];
  const hasRecurringNotes = params.recurringNoteSources.length > 0;
  const hasHistory = params.historyCount > 0;
  const hasCurrentState = params.projectCount > 0 || hasHistory;
  const shouldPreferRecurring = hasRecurringNotes && (hasHistory || params.projectCount > 0 || !hasKickoffSource);
  const recurringActionSources = newerRecurringSources.length ? newerRecurringSources : params.recurringNoteSources;
  const recurringAction = uniqueEventIds(recurringActionSources);
  const contextLine = [
    `${SERVICE_LABELS[params.serviceTab]} service`,
    `Owner ${params.ownerEmail || '--'}`,
    `${params.projectCount} active project${params.projectCount === 1 ? '' : 's'}`,
    `Kickoff ${hasKickoffSource ? formatCompactDate(params.kickoffSource?.note?.updatedAt ?? params.kickoffSource?.event.startsAt) : 'not found'}`,
    `Latest recurring note ${latestRecurringSource ? formatCompactDate(latestRecurringSource.note.updatedAt || latestRecurringSource.event.startsAt) : 'none'}`,
    `Latest saved plan ${params.latestRunSummary ? formatCompactDate(params.latestRunSummary.updatedAt) : 'none'}`,
  ].join(' | ');

  if (!params.hasActiveServiceContract) {
    return {
      state: 'missing_prerequisites',
      title: `${SERVICE_LABELS[params.serviceTab]} planning is blocked`,
      description: `No active ${SERVICE_LABELS[params.serviceTab]} service contract is visible for this organization, so there is no purchased offering available to anchor a Program yet.`,
      contextLine,
      primaryAction: null,
      newerRecurringNoteCount: newerRecurringSources.length,
    };
  }

  if (!hasHistory && shouldPreferRecurring && recurringAction.length) {
    return {
      state: 'new_meeting_notes_detected',
      title: 'Plan from new meeting notes',
      description: 'Recent recurring meeting notes are available. Start Program to choose a purchased offering and create a Program from the newest notes.',
      contextLine,
      primaryAction: {
        kind: 'run_recurring',
        label: 'Start Program',
        scheduleEventIds: recurringAction,
      },
      newerRecurringNoteCount: newerRecurringSources.length || params.recurringNoteSources.length,
    };
  }

  if (!hasHistory && hasKickoffSource) {
    return {
      state: 'first_plan_needed',
      title: `Build ${params.orgName}'s first Program`,
      description: 'A usable onboarding kickoff source is available now. Start Program to choose a purchased offering and create the first Program for that scope.',
      contextLine,
      primaryAction: {
        kind: 'run_onboarding',
        label: 'Start Program',
        scheduleEventIds: [params.kickoffSource?.event.id ?? ''].filter(Boolean),
      },
      newerRecurringNoteCount: newerRecurringSources.length,
    };
  }

  if (shouldPreferRecurring && recurringAction.length && newerRecurringSources.length > 0) {
    return {
      state: 'new_meeting_notes_detected',
      title: 'Plan from new meeting notes',
      description: `${newerRecurringSources.length} newer recurring note${newerRecurringSources.length === 1 ? '' : 's'} were detected after the latest saved version. Start Program to choose a purchased offering, then open the existing Program if you want to update that specific scope.`,
      contextLine,
      primaryAction: {
        kind: 'run_recurring',
        label: 'Update Program',
        scheduleEventIds: recurringAction,
      },
      newerRecurringNoteCount: newerRecurringSources.length,
    };
  }

  if (params.latestRunSummary) {
    const createNewDraft =
      shouldPreferRecurring && recurringAction.length
        ? {
            kind: 'run_recurring' as const,
            label: 'Update Program',
            scheduleEventIds: recurringAction,
          }
        : hasKickoffSource
          ? {
              kind: 'run_onboarding' as const,
              label: 'Update Program',
              scheduleEventIds: [params.kickoffSource?.event.id ?? ''].filter(Boolean),
            }
          : null;
    return {
      state: 'latest_plan_available',
      title: params.programCount > 0
        ? `Manage ${params.orgName}'s programs`
        : `Review ${params.orgName}'s latest draft`,
      description:
        params.programCount > 0
          ? 'Review Programs first, then open the specific Program you want to inspect or update. Starting Program from the main view lets you choose an offering and create another Program for that scope.'
          : params.latestRunSummary.mode === 'existing_client_recurring_planning'
            ? 'The latest saved draft already reflects the most recent recurring planning context available in this surface.'
            : 'The latest saved onboarding draft is still the most current planning view for this organization.',
      contextLine,
      primaryAction: createNewDraft,
      newerRecurringNoteCount: newerRecurringSources.length,
    };
  }

  if (hasCurrentState) {
    return {
      state: 'manage_planning',
      title: `Manage ${params.orgName} planning`,
      description: 'Planning context exists for this organization. Open a Program below to inspect or update it, or start a new Program from one purchased offering.',
      contextLine,
      primaryAction:
        hasRecurringNotes && recurringAction.length
          ? {
              kind: 'run_recurring',
              label: params.programCount > 0 || hasHistory ? 'Update Program' : 'Start Program',
              scheduleEventIds: recurringAction,
            }
          : hasKickoffSource
            ? {
                kind: 'run_onboarding',
                label: params.programCount > 0 || hasHistory ? 'Update Program' : 'Start Program',
                scheduleEventIds: [params.kickoffSource?.event.id ?? ''].filter(Boolean),
              }
            : null,
      newerRecurringNoteCount: newerRecurringSources.length,
    };
  }

  return {
    state: 'missing_prerequisites',
    title: 'Planning needs source material',
    description: 'No usable kickoff source or recurring meeting notes are available yet for this service. Capture a persisted meeting note in Meetings before starting planning from this surface.',
    contextLine,
    primaryAction: null,
    newerRecurringNoteCount: newerRecurringSources.length,
  };
}
